<script setup>
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import authV2ForgotPasswordIllustrationDark from '@images/pages/auth-v2-forgot-password-illustration-dark.png'
import authV2ForgotPasswordIllustrationLight from '@images/pages/auth-v2-forgot-password-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { integerValidator } from '@/@core/utils/validators'
import { useRouter } from 'vue-router'
import { useGlobalVariablesStore } from '@/composables/useGlobalStore'
import UsersController from '@/Controller/users'
import { useLocalizedValidators } from '@/composables/validators'
import { computed } from 'vue'
import { getCurrentInstance } from 'vue'
import { sha256 } from 'js-sha256'
import { useToast } from 'vue-toastification'

const { localizedRequired, localizedPassword } = useLocalizedValidators()

const router = useRouter()
const mobile = ref('')
const authThemeImg = useGenerateImageVariant(authV2ForgotPasswordIllustrationLight, authV2ForgotPasswordIllustrationDark)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
const globalVariables=useGlobalVariablesStore()
const { proxy } = getCurrentInstance()
const toast=useToast()

const hashPassword = password => {
  return sha256(password)
}

const form = ref({
  password: '',
  confirm: '',
})

const errors = ref({
  confirm: undefined,
  password: undefined,
})

const isPasswordVisible = ref(false)

const passwordMatchValidator= value=>form.value.password==form.value.confirm ? true : proxy.$t('Password and confirm password are not matched')
const passwordIsValid=computed(()=>passwordMatchValidator(form.value.password) === true && localizedRequired(form.value.password) === true && localizedPassword(form.value.password) === true)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const storePassword=()=>{
  let hashedPassword=hashPassword(form.value.password)
  try{
    UsersController.changePassword(globalVariables.mobileToVerify, hashedPassword)
    toast.success(proxy.$t('Password stored successfully'))
    router.push('/login')
  }catch(ex){
    console.log(ex)
    
    toast.error(proxy.$t('Password didnot stored'))
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ $t(themeConfig.app.title) }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper bg-surface"
    no-gutters
  >
    <VCol
      cols="12"
      md="8"
      class="d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1 text-center">
            {{$t('Change Password')}}
          </h4>
          <p class="mb-0 mt-3 text-center">
            {{$t("Please Enter Your New Password.")}}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="() => {}">
            <VRow>
              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :label="$t('Password')"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  prepend-inner-icon="tabler-key"
                  :rules="[localizedRequired, localizedPassword]"
                  :error-messages="errors.password"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <!-- confirm -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.confirm"
                  :label="$t('Confirm Password')"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  prepend-inner-icon="tabler-key"
                  :rules="[localizedRequired, localizedPassword, passwordMatchValidator]"
                  :error-messages="errors.confirm"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <!-- Reset link -->
              <VCol cols="12">
                <VBtn
                  block
                  class="rounded-lg"
                  :color="passwordIsValid!==true ? 'secondary':'primary'"
                  :disabled="passwordIsValid!==true"
                  type="submit"
                  @click="storePassword"
                >
                  {{ $t('Confirm') }}
                </VBtn>
              </VCol>

              <!-- back to login -->
              <VCol cols="12">
                <RouterLink
                  class="d-flex"
                  :to="{ name: 'login' }"
                >
                  <VIcon
                    icon="tabler-transfer"
                    size="20"
                    class="me-1 flip-in-rtl"
                  />
                  <span>{{$t('Back to login')}}</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      md="4"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
        >
          <VImg
            max-width="468"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
